import { useState } from 'react';

import { Dialog, Transition } from '@headlessui/react';
import { BuildingOffice2Icon } from '@heroicons/react/24/outline';

import Button, { BUTTON_KIND } from '@/components/Button';

import { OnboardingPositionChoice } from './onboardingApi';

function CompanyLogo({
  src,
  alt,
}: {
  src: string | undefined;
  alt: string;
}) {
  const [failed, setFailed] = useState(false);

  if (!src || failed) {
    return <BuildingOffice2Icon className="w-6 h-6 text-gray-400" />;
  }

  return (
    <img
      src={src}
      alt={alt}
      className="w-full h-full object-cover"
      onError={() => setFailed(true)}
    />
  );
}

interface Props {
  isOpen: boolean;
  positions: OnboardingPositionChoice[];
  onSelect: (position: OnboardingPositionChoice) => void;
  onClose: () => void;
  loading?: boolean;
}

export default function CompanySelectionModal({
  isOpen,
  positions,
  onSelect,
  onClose,
  loading = false,
}: Props) {
  const [selected, setSelected] = useState<OnboardingPositionChoice | null>(
    positions[0] ?? null,
  );

  const handleConfirm = () => {
    if (selected) onSelect(selected);
  };

  return (
    <Transition appear show={isOpen}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        {/* Backdrop */}
        <Transition.Child
          enter="ease-out duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/40" />
        </Transition.Child>

        <div className="fixed inset-0 flex items-center justify-center p-4">
          <Transition.Child
            enter="ease-out duration-200"
            enterFrom="opacity-0 scale-95"
            enterTo="opacity-100 scale-100"
            leave="ease-in duration-150"
            leaveFrom="opacity-100 scale-100"
            leaveTo="opacity-0 scale-95"
          >
            <Dialog.Panel className="w-full max-w-lg rounded-xl bg-white shadow-xl p-6">
              <Dialog.Title className="text-lg font-semibold text-gray-900">
                Select your company
              </Dialog.Title>
              <Dialog.Description className="mt-1 text-sm text-gray-500">
                We found multiple current positions on your LinkedIn. Select
                which company you&apos;re creating this profile for.
              </Dialog.Description>

              <div className="mt-5 space-y-3 max-h-80 overflow-y-auto pr-1">
                {positions.map((position, i) => {
                  const isSelected = selected === position;
                  return (
                    <button
                      key={i}
                      type="button"
                      onClick={() => setSelected(position)}
                      className={`w-full flex items-center gap-4 rounded-lg border-2 p-4 text-left transition-colors ${
                        isSelected
                          ? 'border-brand-500 bg-brand-50'
                          : 'border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      <div className="flex-shrink-0 w-12 h-12 rounded-full border border-gray-200 bg-gray-50 flex items-center justify-center overflow-hidden">
                        <CompanyLogo
                          src={position.logoUrl ?? undefined}
                          alt={position.companyName ?? ''}
                        />
                      </div>

                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-gray-900 truncate">
                          {position.companyName ?? 'Unknown company'}
                        </p>
                        {position.title && (
                          <p className="text-sm text-gray-500 truncate">
                            {position.title}
                          </p>
                        )}
                        {position.companyLinkedInUrl && (
                          <p className="text-xs text-brand-500 truncate mt-0.5">
                            {position.companyLinkedInUrl}
                          </p>
                        )}
                      </div>

                      <div
                        className={`flex-shrink-0 w-4 h-4 rounded-full border-2 ${
                          isSelected
                            ? 'border-brand-500 bg-brand-500'
                            : 'border-gray-300 bg-white'
                        }`}
                      />
                    </button>
                  );
                })}
              </div>

              <div className="mt-6 flex justify-end gap-3">
                <Button
                  buttonText="Cancel"
                  kind={BUTTON_KIND.WHITE}
                  onClick={onClose}
                />
                <Button
                  buttonText="Continue"
                  kind={BUTTON_KIND.PRIMARY}
                  onClick={handleConfirm}
                  disabled={!selected || loading}
                  loading={loading}
                />
              </div>
            </Dialog.Panel>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition>
  );
}
